import { useState, useRef, useEffect } from "react";
import { ChevronDown, FileText, BookOpen, ArrowRight, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

interface FeaturedArticle {
  slug: string;
  title: string;
  read_time: string;
  image_url: string | null;
}

const ResourcesMegaMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [featuredArticles, setFeaturedArticles] = useState<FeaturedArticle[]>([]);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchFeaturedArticles();
  }, []);

  const fetchFeaturedArticles = async () => {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('slug, title, read_time, image_url')
        .eq('featured', true)
        .limit(2);

      if (error) throw error;
      
      // If no featured articles, get the latest 2
      if (!data || data.length === 0) {
        const { data: latestData } = await supabase
          .from('blog_posts')
          .select('slug, title, read_time, image_url')
          .order('created_at', { ascending: false })
          .limit(2);
        
        setFeaturedArticles(latestData || []);
      } else {
        setFeaturedArticles(data);
      }
    } catch (error) {
      console.error('Error fetching featured articles:', error);
    }
  };

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 200);
  };

  const handleNavigate = (href: string) => {
    navigate(href);
    setIsOpen(false);
  };

  return (
    <div className="relative hidden md:block" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <button className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-foreground hover:text-foreground/80 transition-colors rounded-full hover:bg-muted/50 outline-none">
        Resources
        <ChevronDown className={`h-3 w-3 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {/* Mega Menu */}
      <div className={`absolute top-full left-1/2 -translate-x-1/2 pt-3 z-50 transition-all duration-300 ${isOpen ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-3 pointer-events-none"}`}>
        <div className="bg-card border border-border rounded-2xl shadow-2xl w-[620px] p-5 backdrop-blur-xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-border/50">
            <h3 className="text-sm font-semibold text-foreground">Resources</h3>
            <span className="text-xs text-muted-foreground">Docs & insights</span>
          </div>

          <div className="grid grid-cols-5 gap-4">
            {/* Left Column - Main Links */}
            <div className="col-span-2 space-y-2">
              {/* Documentation Card */}
              <a 
                href="https://depin-land.gitbook.io/depin-land"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsOpen(false)}
                className="group w-full p-3 rounded-xl border border-border/50 bg-muted/30 hover:bg-muted/50 transition-all duration-300 hover:border-muted-foreground/30 text-left block" 
                style={{ animation: isOpen ? 'dropdownItemIn 0.4s ease-out 0ms both' : 'none' }}
              >
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-foreground/10 transition-all duration-300 group-hover:bg-foreground/20 group-hover:scale-110">
                    <FileText className="h-4 w-4 text-foreground" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-foreground group-hover:text-foreground/80 transition-colors">
                        Documentation
                      </span>
                      <ArrowRight className="h-3 w-3 text-muted-foreground transition-transform duration-300 group-hover:translate-x-1 group-hover:text-foreground" />
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Our mission, vision, guides, and integration docs.
                    </p>
                  </div>
                </div>
              </a>

              {/* Blog Card */}
              <button 
                onClick={() => handleNavigate("/blog")} 
                className="group w-full p-3 rounded-xl border border-border/50 bg-muted/30 hover:bg-muted/50 transition-all duration-300 hover:border-muted-foreground/30 text-left" 
                style={{ animation: isOpen ? 'dropdownItemIn 0.4s ease-out 100ms both' : 'none' }}
              >
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-foreground/10 transition-all duration-300 group-hover:bg-foreground/20 group-hover:scale-110">
                    <BookOpen className="h-4 w-4 text-foreground" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-foreground group-hover:text-foreground/80 transition-colors">
                        Blog
                      </span>
                      <ArrowRight className="h-3 w-3 text-muted-foreground transition-transform duration-300 group-hover:translate-x-1 group-hover:text-foreground" />
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Latest news, insights, and DePIN industry updates.
                    </p>
                  </div>
                </div>
              </button>
            </div>

            {/* Right Column - Featured Posts */}
            <div className="col-span-3 space-y-3" style={{ animation: isOpen ? 'dropdownItemIn 0.4s ease-out 150ms both' : 'none' }}>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Featured Articles</p>
              
              {featuredArticles.length > 0 ? (
                featuredArticles.map((article) => (
                  <button 
                    key={article.slug}
                    onClick={() => handleNavigate(`/blog/${article.slug}`)} 
                    className="group w-full flex gap-3 p-2 rounded-xl hover:bg-muted/30 transition-all duration-300 text-left"
                  >
                    <div className="relative w-20 h-14 rounded-lg overflow-hidden flex-shrink-0 bg-muted/50">
                      {article.image_url ? (
                        <img 
                          src={article.image_url} 
                          alt={article.title} 
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" 
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <BookOpen className="h-5 w-5 text-muted-foreground/50" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-xs font-medium text-foreground group-hover:text-foreground/80 transition-colors line-clamp-2 mb-1">
                        {article.title}
                      </h4>
                      <div className="flex items-center gap-2">
                        <Clock className="h-2.5 w-2.5 text-muted-foreground" />
                        <span className="text-[10px] text-muted-foreground">{article.read_time}</span>
                      </div>
                    </div>
                  </button>
                ))
              ) : (
                <div className="text-xs text-muted-foreground py-4 text-center">
                  No featured articles yet
                </div>
              )}

              {/* View All Link */}
              <button 
                onClick={() => handleNavigate("/blog")} 
                className="flex items-center gap-1 text-xs font-medium text-foreground hover:text-foreground/80 hover:underline px-2 transition-colors"
              >
                View all articles
                <ArrowRight className="h-3 w-3" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResourcesMegaMenu;
